import type { Id } from "@repo/convex/dataModel";

export function uploadToConvexStorage(
  uploadUrl: string,
  file: File,
  onProgress: (progress: number) => void = () => {},
  signal?: AbortSignal
): Promise<Id<"_storage">> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    const abortUpload = () => {
      xhr.abort();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    signal?.addEventListener("abort", abortUpload, { once: true });

    const cleanup = () => {
      signal?.removeEventListener("abort", abortUpload);
    };

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const nextProgress = Math.max(0, Math.min(1, event.loaded / event.total));
      onProgress(nextProgress);
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error(`Network error while uploading ${file.name}`));
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Upload failed (${xhr.status}) for ${file.name}`));
        return;
      }

      try {
        const parsed = JSON.parse(xhr.responseText) as { storageId?: string };
        if (!parsed.storageId) {
          reject(new Error(`Missing storageId in upload response for ${file.name}`));
          return;
        }
        resolve(parsed.storageId as Id<"_storage">);
      } catch (error) {
        reject(
          new Error(
            error instanceof Error
              ? `Invalid upload response for ${file.name}: ${error.message}`
              : `Invalid upload response for ${file.name}`
          )
        );
      }
    };

    xhr.send(file);
  });
}
